/* Zivol Arc — picker, gallery sync, sticky bar, chat parking.
 *
 * ONE SOURCE OF TRUTH. Every control writes to the same hidden variant input
 * inside the one real <form>. The sticky bar does not carry its own buy logic —
 * it calls requestSubmit() on that form. Two forms would mean two answers to
 * "what am I buying", which is how a wrong id gets posted.
 *
 * Prices, savings and per-unit figures come from Liquid, computed from live
 * variant prices. Nothing here invents a number.
 */
(() => {
  const root = document.querySelector('[data-arc-root]');
  if (!root) return;

  const dataEl = root.querySelector('[data-arc-variants]');
  let variants = [];
  try { variants = JSON.parse(dataEl?.textContent || '[]'); } catch { /* fall through */ }
  const byId = (id) => variants.find((v) => String(v.id) === String(id)) || null;

  const idInput   = root.querySelector('[data-arc-variant-id]');
  const buyBtn    = root.querySelector('[data-arc-buy]');
  const priceEl   = root.querySelector('[data-arc-price]');
  const wasEl     = root.querySelector('[data-arc-was]');
  const saveEl    = root.querySelector('[data-arc-save]');
  const onePrice  = root.querySelector('[data-arc-one-price]');
  const form      = root.querySelector('form[action*="/cart/add"]');

  const tierInputs   = [...root.querySelectorAll('input[name="arc-tier"]')];
  const singleInputs = [...root.querySelectorAll('input[name="arc-single"]')];
  const sel = (list) => list.find((i) => i.checked);

  const money = (v) => (v ? v.price : '');

  function currentVariant() {
    const tier = sel(tierInputs);
    if (!tier) return null;
    if (tier.dataset.arcKind === 'set') return byId(tier.value);
    return byId(sel(singleInputs)?.value);
  }

  const mixBlocks = [...root.querySelectorAll('[data-arc-mix]')];

  /* Both cards name their inputs `properties[Tray 1]` / `[Tray 2]`. If the
     inactive card's chips stayed enabled they would submit too and overwrite
     the active card's values — so everything outside the chosen tier is
     disabled, and a disabled control is not submitted at all. */
  function syncMix(kind) {
    mixBlocks.forEach((block) => {
      const active = block.getAttribute('data-arc-mix') === kind;
      block.hidden = !active;
      // The hidden input is the thing that posts, so that is the thing that
      // must be disabled — a disabled control is not submitted at all.
      block.querySelectorAll('[data-arc-slot-value]').forEach((h) => { h.disabled = !active; });
      block.querySelectorAll('[data-arc-chip]').forEach((chip) => { chip.disabled = !active; });
      if (!active) closeSlots(block);
    });
  }

  function closeSlots(scope, except) {
    scope.querySelectorAll('[data-arc-slot]').forEach((slot) => {
      if (slot === except) return;
      const head = slot.querySelector('[data-arc-slot-toggle]');
      const panel = slot.querySelector('.arc-slot__panel');
      if (head) head.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  }

  /* The head line restates the slot's choice so the card is readable closed,
     and the card's combo line restates all of them so the buyer can check the
     set before adding — both read the same checked inputs the form posts. */
  function paintSlot(slot) {
    const checked = slot.querySelector('[data-arc-chip]:checked');
    if (!checked) return;
    const hidden = slot.querySelector('[data-arc-slot-value]');
    const dot = slot.querySelector('[data-arc-slot-dot]');
    const word = slot.querySelector('[data-arc-slot-word]');
    if (hidden) hidden.value = checked.value;
    if (dot) dot.style.setProperty('--arc-chip-c', `var(${checked.dataset.arcChipToken})`);
    if (word) word.textContent = checked.dataset.arcChipShort;
  }

  function comboText(kind) {
    const block = mixBlocks.find((b) => b.getAttribute('data-arc-mix') === kind);
    if (!block) return '';
    return [...block.querySelectorAll('[data-arc-chip]:checked')]
      .map((c) => c.dataset.arcChipShort)
      .join(' · ');
  }

  function paintCombo(kind) {
    const line = root.querySelector(`[data-arc-combo="${kind}"]`);
    if (line) line.textContent = comboText(kind);
  }

  /* Both cards stay on screen whichever tier is chosen, so both lines are
     painted — otherwise the unselected card keeps its server-rendered wording
     and the two read in different formats side by side. */
  function paintAllCombos() {
    mixBlocks.forEach((b) => paintCombo(b.getAttribute('data-arc-mix')));
  }

  function paintCards() {
    tierInputs.forEach((i) => {
      const card = i.closest('[data-arc-tier]');
      if (card) card.classList.toggle('is-selected', i.checked);
    });
  }

  function apply(dispatch) {
    paintCards();
    const tier = sel(tierInputs);
    const isOne = tier && tier.dataset.arcKind !== 'set';
    const kind = tier ? (tier.id.includes('trio') ? 'trio' : tier.id.includes('pair') ? 'pair' : 'one') : 'one';
    syncMix(kind);
    paintAllCombos();
    const v = currentVariant();

    // The singles card shows "from $43" until it is the active choice, then the
    // real price of the selected chip — in place, so the change is visible.
    if (onePrice) {
      const chosen = byId(sel(singleInputs)?.value);
      onePrice.textContent = isOne && chosen ? chosen.price : onePrice.dataset.arcFrom || onePrice.textContent;
    }

    if (!v || !v.available) {
      if (idInput) idInput.value = '';
      if (buyBtn) { buyBtn.disabled = true; buyBtn.textContent = v ? 'Sold out' : 'Choose an option'; }
      syncSticky(null);
      return;
    }

    if (idInput) idInput.value = String(v.id);
    if (buyBtn) { buyBtn.disabled = false; buyBtn.textContent = `Add to cart — ${v.price}`; }
    if (priceEl) priceEl.textContent = v.price;
    if (wasEl) { if (v.was) { wasEl.textContent = v.was; wasEl.hidden = false; } else wasEl.hidden = true; }
    if (saveEl) { if (v.save) { saveEl.textContent = `Save ${v.save}`; saveEl.hidden = false; } else saveEl.hidden = true; }

    syncSticky(v);
    if (dispatch) moveGallery(v.id);
  }

  /* --- gallery ------------------------------------------------------------
     Client-side only. This used to fetch a full section render and hand it to
     the theme via StandardEvents.productSelect, which made media-gallery.js
     replace itself with a rebuilt gallery — a request per chip, the element
     thrown away, and the buyer's position in the strip reset as a side effect.

     Now: look up the variant's media id, find the slide already carrying it,
     and drive the theme's own `select(index)` — the same method the thumbnail
     strip calls via `on:click="/select/N"`. Nothing is fetched.

     Only ever called with dispatch = true, i.e. on a real variant change, so it
     cannot fire on an unrelated re-render. */
  function moveGallery(variantId) {
    const section = root.closest('.shopify-section');
    if (!section) return;

    const slideshow = section.querySelector('slideshow-component');
    const gallery = section.querySelector('media-gallery');
    if (!slideshow || typeof slideshow.select !== 'function' || !gallery) return;

    const variant = byId(variantId);
    if (!variant || !variant.mediaId) return;

    // Scope to the main gallery: media-gallery also renders a hidden zoom
    // dialog holding a duplicate slideshow, and a section-wide query matches
    // those slides too.
    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${variant.mediaId}"]`));
    if (index < 0) return;

    slideshow.select(index);
  }

  /* NO SCROLL-INTO-VIEW ON VARIANT CHANGE — deliberately removed 2026-08-21.
  
     A version of this brought the gallery back into view on mobile after a chip
     change. It worked, but measured at 380x820 the page had to move ~420px and
     the chips went off-screen — the gallery is ~410px tall and the chips sit
     ~1400px down, so the two genuinely cannot share a screen. That takes the
     control away from the buyer at the moment they are using it: anyone
     comparing two finishes has to scroll back down for every comparison.
  
     The owner's call, and the right one: not seeing the change immediately is
     the lesser cost. The real fix is the gallery's POSITION relative to the buy
     column on mobile, which is a layout question, not a scroll one. It stays
     queued (raised 2026-08-16). Do not reintroduce a scroll here. */

  /* --- sticky bar --------------------------------------------------------- */
  const sticky   = document.querySelector('[data-arc-sticky]');
  const sTitle   = sticky?.querySelector('[data-arc-sticky-title]');
  const sPrice   = sticky?.querySelector('[data-arc-sticky-price]');
  const sBtn     = sticky?.querySelector('[data-arc-sticky-btn]');
  const sThumb   = sticky?.querySelector('[data-arc-sticky-thumb]');

  function syncSticky(v) {
    if (!sticky) return;
    if (!v) { sBtn && (sBtn.disabled = true); return; }
    // Full selection, not an abbreviation — the buyer should never wonder what
    // is about to go in the bag.
    if (sTitle) sTitle.textContent = v.isSet ? v.shortTitle : v.title;
    if (sThumb && v.thumb) sThumb.src = v.thumb;
    if (sPrice) sPrice.textContent = v.price;
    if (sBtn) sBtn.disabled = false;
  }

  if (sticky && form) {
    // Submits the REAL form — the bar never holds its own variant id.
    sBtn?.addEventListener('click', () => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
    const buyCol = root.querySelector('.arc-hero__buy');
    if (buyCol && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => { for (const e of entries) sticky.classList.toggle('is-visible', !e.isIntersecting); },
        { threshold: 0 }
      ).observe(buyCol);
    }
  }

  /* --- pending state ------------------------------------------------------ */
  if (form && buyBtn) {
    form.addEventListener('submit', () => {
      buyBtn.setAttribute('aria-busy', 'true');
      sBtn?.setAttribute('aria-busy', 'true');
      window.setTimeout(() => {
        buyBtn.removeAttribute('aria-busy');
        sBtn?.removeAttribute('aria-busy');
      }, 6000);
    });
  }

  /* --- chat parking ------------------------------------------------------- */
  const buyCol = root.querySelector('.arc-hero__buy');
  if (buyCol && 'IntersectionObserver' in window) {
    const park = (on) => document.body.classList.toggle('arc-chat-parked', on);
    park(true);
    new IntersectionObserver(
      (entries) => { for (const e of entries) park(e.isIntersecting); },
      { rootMargin: '0px 0px 400px 0px', threshold: 0 }
    ).observe(buyCol);
  }

  /* --- wiring ------------------------------------------------------------- */
  tierInputs.forEach((i) => i.addEventListener('change', () => apply(true)));
  mixBlocks.forEach((block) => {
    const kind = block.getAttribute('data-arc-mix');

    block.querySelectorAll('[data-arc-slot-toggle]').forEach((head) => {
      head.addEventListener('click', () => {
        const slot = head.closest('[data-arc-slot]');
        const panel = slot.querySelector('.arc-slot__panel');
        const open = head.getAttribute('aria-expanded') === 'true';
        closeSlots(block, slot);
        head.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    });

    block.querySelectorAll('[data-arc-chip]').forEach((chip) => {
      chip.addEventListener('change', () => {
        const slot = chip.closest('[data-arc-slot]');
        paintSlot(slot);
        paintCombo(kind);
        // Choosing is the end of the interaction: collapse back to the line so
        // the card returns to its readable state instead of staying opened out.
        const head = slot.querySelector('[data-arc-slot-toggle]');
        head.setAttribute('aria-expanded', 'false');
        slot.querySelector('.arc-slot__panel').hidden = true;
      });
    });

    block.querySelectorAll('[data-arc-slot]').forEach(paintSlot);
  });
  singleInputs.forEach((i) =>
    i.addEventListener('change', () => {
      // Choosing a chip means choosing "One" — otherwise the chip would change
      // nothing visible while a set stayed selected.
      const one = tierInputs.find((t) => t.dataset.arcKind !== 'set');
      if (one) one.checked = true;
      apply(true);
    })
  );

  if (onePrice) onePrice.dataset.arcFrom = onePrice.textContent.trim();
  apply(false);
})();
